import { SignContractDialog } from '@/components/contracts/SignContractDialog';
import { Button } from '@/components/ui';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { inlineSignStart } from '../../../api/rentals';

/**
 * ADR-103 Am-6 — inline-sign „Aláírás most" CTA (X'-flow, #316).
 *
 * Create-then-sign EGY lépésben, navigálás nélkül: a gomb a thin
 * `inlineSignStart` (get-or-create contract a rentalId-n) után RÖGTÖN megnyitja a
 * meglévő `SignContractDialog`-ot (ADR-099 esign-session + SignaturePad + 2
 * consent + submit) → `RentalContract.signedAt` (Am-5). Az esign/consent flow a
 * meglévő, verifikált stacken megy (0 duplikáció, compliance-megőrzés).
 *
 * Placement: RentalDetailPage, DRAFT rentalon (a hívó gate-eli).
 */
export function InlineSignButton({
  rentalId,
  onSigned,
}: {
  rentalId: string;
  onSigned?: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [contract, setContract] = useState<{
    id: string;
    contractNumber: string;
    type: string;
  } | null>(null);

  const handleStart = async (): Promise<void> => {
    setBusy(true);
    try {
      // X' 1. lépés: thin get-or-create contract (idempotens a rentalId-n a BE-ben;
      // a key csak wizard-szintű retry-guard).
      const { data } = await inlineSignStart(rentalId, `inline-sign-${rentalId}`);
      setContract({ id: data.contractId, contractNumber: data.contractNumber, type: 'rental' });
      setSignOpen(true);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('rental.inlineSign.startFailed'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        disabled={busy}
        onClick={() => void handleStart()}
        data-testid="inline-sign-btn"
      >
        {t('rental.inlineSign.cta')}
      </Button>

      {contract && (
        <SignContractDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          contract={contract}
          onSigned={() => {
            setSignOpen(false);
            onSigned?.();
          }}
        />
      )}
    </>
  );
}
